import { create } from 'zustand'
import { authApi } from '@/services/api'
import { clearSessionState } from '@/utils/session'

// Impersonation parks the original admin's token + identity in localStorage.
// On exit, we swap them back so the admin lands back where they were.
const IMP_KEY = 'scribenza_impersonation'

const loadImpersonation = () => {
  try { return JSON.parse(localStorage.getItem(IMP_KEY) || 'null') } catch { return null }
}

export const useAuthStore = create((set, get) => ({
  user:    null,
  token:   localStorage.getItem('scribenza_token'),
  loading: true,
  impersonation: loadImpersonation(),  // { admin: {...}, original_token } when active

  setUser:  (user)  => set({ user }),
  setToken: (token) => {
    localStorage.setItem('scribenza_token', token)
    set({ token })
  },

  login: async ({ token, user }) => {
    await clearSessionState()
    localStorage.removeItem(IMP_KEY)
    localStorage.setItem('scribenza_token', token)
    set({ user, token, loading: false, impersonation: null })
  },

  logout: async () => {
    try { await authApi.logout() } catch {}
    await clearSessionState()
    localStorage.removeItem(IMP_KEY)
    set({ user: null, token: null, loading: false, impersonation: null })
  },

  /**
   * Begin impersonating another user. The current admin token is preserved
   * in localStorage so exitImpersonation() can restore it without a re-login.
   */
  startImpersonation: ({ token, user, impersonator }) => {
    const originalToken = localStorage.getItem('scribenza_token')
    const payload = { admin: impersonator, original_token: originalToken }
    localStorage.setItem(IMP_KEY, JSON.stringify(payload))
    localStorage.setItem('scribenza_token', token)
    set({ user, token, impersonation: payload })
  },

  /**
   * End the current impersonation and restore the admin's session in place.
   * Falls back to a full logout if the original token has been wiped.
   */
  exitImpersonation: async () => {
    const imp = get().impersonation
    if (!imp?.original_token) {
      await get().logout()
      return
    }
    localStorage.setItem('scribenza_token', imp.original_token)
    localStorage.removeItem(IMP_KEY)
    try {
      const res = await authApi.me()
      set({ user: res.data.user, token: imp.original_token, impersonation: null })
    } catch {
      await get().logout()
    }
  },

  init: async () => {
    const token = localStorage.getItem('scribenza_token')
    if (!token) { set({ loading: false }); return }
    try {
      const res = await authApi.me()
      set({ user: res.data.user, token, loading: false, impersonation: loadImpersonation() })
    } catch {
      await clearSessionState()
      localStorage.removeItem(IMP_KEY)
      set({ user: null, token: null, loading: false, impersonation: null })
    }
  },
}))
